import { PANEL_TYPES, PANEL_STATES } from "../projectWizardConstants.js";
import { OPERATIONS } from "../../../../constants/operations.js";

import * as Yup from "yup";
import * as Schemas from "../../../../components/forms/validationSchemas.js";

import {
  nameValidationScheme,
  descriptionValidationScheme
} from "../../../../components/forms/formik/FormikDetailsPanel/detailsPanelValidationSchemas.js";

import { resourceBlockValidationSchema } from "../../../../components/projectWizard/resourcePanel/resourceBlockValidationSchema.js";
import { sshTableValidationSchema } from "../../../../components/sshKeys/sshTableValidationSchemas.js";
import { CIDRPanelValidationSchema } from "../../../../components/projectWizard/CIDRPanel/CIDRPanelValidationSchemas.js";
import { vpnBlockValidationSchema } from "../../../../components/vpns/vpnBlockValidationSchema.js";

export default {
  id: "born-in-the-cloud",
  type: "STEPPED_BORN_IN_THE_CLOUD",
  copyKey: "project_wizard.config_copy.born_in_the_cloud",

  initialValues: {
    name: "",
    description: "",
    provider: null,
    region: null,
    zone: null,

    workstations: [],
    containers: [],
    databases: [],
    nodes: [],

    cidrClassPrefix: "10.0",
    cidrClassPrefixPrime: null,
    cidrClassPrefixSub: null,
    sshKeys: PANEL_STATES.SKIPPED,
    requiresUserVerification: 0,
    virtualPrivateNetworkGateways: [],
    snowflakes: []
  },

  estimateConditions: ["details", "resources", "infrastructure"],

  steps: [
    {
      id: "details",
      name: "Project Details",
      icon: "folder",
      panels: [
        {
          id: "details",
          panelType: PANEL_TYPES.DETAILS,
          permissionType: OPERATIONS.CREATE,
          required: true,
          view: PANEL_STATES.ACTIVE,
          projectName: null,
          projectDescription: null
        },
        {
          id: "providers",
          panelType: PANEL_TYPES.PROVIDER,
          permissionType: OPERATIONS.LIST,
          required: true,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "regions",
          panelType: PANEL_TYPES.REGION,
          permissionType: OPERATIONS.LIST,
          required: true,
          view: PANEL_STATES.INACTIVE,
          dependencies: [
            {
              name: "provider",
              view: PANEL_STATES.ACTIVE
            }
          ]
        }
      ],
      panelDependencyIndex: ["provider"],
      validationSchema: function (messages) {
        const objShape = {
          projectId: Yup.mixed().when("name", {
            is: name => !name,
            then: Yup.mixed().required()
          }),
          name: Yup.string().when("projectId", {
            is: projectId => !projectId,
            then: nameValidationScheme(messages)
          }),
          description: Yup.string().when("projectId", {
            is: projectId => !projectId,
            then: descriptionValidationScheme(messages)
          }),
          provider: Schemas.dropdownSchema("Provider", messages),
          region: Schemas.dropdownSchema("Location", messages),
          zone: Schemas.dropdownSchema("Zone", messages, false)
        };
        return Yup.object().shape(objShape, ["projectId", "name", "provider"]);
      }
    },
    {
      id: "resources",
      name: "Resources",
      icon: "desktop",
      panels: [
        {
          id: "workstations",
          panelType: PANEL_TYPES.WORKSTATION,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "containers",
          panelType: PANEL_TYPES.STORAGE,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "databases",
          panelType: PANEL_TYPES.RENDER_MANAGER,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "nodes",
          panelType: PANEL_TYPES.NODE,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        }
      ],
      validationSchema: function (messages) {
        return Yup.object().shape({
          workstations: Yup.array().of(resourceBlockValidationSchema(messages)),
          containers: Yup.array().of(resourceBlockValidationSchema(messages)),
          databases: Yup.array().of(resourceBlockValidationSchema(messages)),
          nodes: Yup.array().of(resourceBlockValidationSchema(messages))
        });
      }
    },
    {
      id: "infrastructure",
      name: "Infrastructure",
      icon: "sitemap",
      panels: [
        {
          id: "sshKeys",
          panelType: PANEL_TYPES.SSH,
          permissionType: OPERATIONS.CREATE,
          required: true,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "requiresUserVerification",
          panelType: PANEL_TYPES.OTP,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "cidrClassPrefix",
          panelType: PANEL_TYPES.CIDR,
          permissionType: OPERATIONS.CREATE,
          required: true,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "virtualPrivateNetworkGateways",
          panelType: PANEL_TYPES.VPN_GATEWAY,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "snowflakes",
          panelType: PANEL_TYPES.SNOWFLAKE,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        }
      ],
      validationSchema: function (messages) {
        return Yup.object().shape({
          sshKeys: Yup.mixed().when("projectId", {
            is: projectId => !projectId,
            then: sshTableValidationSchema(messages)
          }),
          cidrClassPrefix: Yup.mixed().when("projectId", {
            is: projectId => !projectId,
            then: CIDRPanelValidationSchema(messages)
          }),
          virtualPrivateNetworkGateways: Yup.array().of(
            vpnBlockValidationSchema(messages)
          ),
          snowflakes: Yup.array().of(resourceBlockValidationSchema(messages))
        });
      }
    },
    {
      id: "summary",
      name: "Summary",
      icon: "list",
      defaultEstimatePanel: false,
      panels: [
        {
          id: "summary",
          panelType: PANEL_TYPES.SUMMARY,
          permissionType: OPERATIONS.CREATE,
          required: false,
          view: PANEL_STATES.ACTIVE
        },
        {
          id: "estimate",
          panelType: PANEL_TYPES.ESTIMATE,
          view: PANEL_STATES.ACTIVE,
          options: {
            showDetails: true,
            controlsEnabled: false
          }
        }
      ]
    }
  ]
};
